import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Building2, Landmark, Hospital, Train, Handshake } from 'lucide-react';
import { heroData, servicesData, coreStrengthsData, majorCustomersData, partnerCompaniesData } from '../data/mock';

const Home = () => {
  return (
    <div>
      {/* Hero Section */}
      <section className="section-padding" style={{ background: 'var(--bg-primary)' }}>
        <div className="container">
          <div className="grid-two-column">
            <div>
              <h1 className="hero-large mb-6">{heroData.title}</h1>
              <p className="body-large mb-4" style={{ color: 'var(--text-secondary)' }}>
                {heroData.subtitle}
              </p>
              <p className="body-regular mb-8" style={{ color: 'var(--text-secondary)' }}>
                {heroData.description}
              </p>
              <Link to="/contact" className="btn-primary">
                {heroData.ctaText}
              </Link>
            </div>
            <div>
              <img 
                src={heroData.image} 
                alt="Modern Healthcare Facility"
                style={{ 
                  width: '100%', 
                  height: '500px', 
                  objectFit: 'cover',
                  borderRadius: '0px'
                }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Services Overview */}
      <section className="section-padding" style={{ background: 'var(--bg-secondary)' }}>
        <div className="container">
          <div className="text-center mb-12">
            <h2 className="hero-medium mb-4">Comprehensive Healthcare Solutions</h2>
            <p className="body-large" style={{ color: 'var(--text-secondary)', maxWidth: '800px', margin: '0 auto' }}>
              Modular turnkey solutions in EPC mode from conceptualization to long-term operations
            </p>
          </div>

          <div className="grid-product-showcase">
            {servicesData.map((service) => (
              <div key={service.id} className="product-card">
                <img 
                  src={service.image} 
                  alt={service.title}
                  className="product-card-image"
                />
                <h3 className="product-card-title">{service.title}</h3>
                <p className="product-card-description">{service.description}</p>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/services" className="btn-secondary">
              View All Services
              <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* Core Strengths Highlight */}
      <section className="section-padding" style={{ background: 'var(--bg-primary)' }}>
        <div className="container">
          <div className="text-center mb-12">
            <h2 className="hero-medium mb-4">Why Choose Pinnacle Biomed</h2>
            <p className="body-large" style={{ color: 'var(--text-secondary)', maxWidth: '800px', margin: '0 auto' }}>
              Decades of proven experience delivering healthcare infrastructure excellence
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {coreStrengthsData.slice(0, 6).map((strength) => (
              <div 
                key={strength.id}
                style={{
                  background: 'var(--bg-secondary)',
                  padding: '32px',
                  borderRadius: '0px',
                  border: '1px solid var(--border-light)'
                }}
              >
                <h3 className="heading-2 mb-3">{strength.title}</h3>
                <p className="body-regular" style={{ color: 'var(--text-secondary)' }}>
                  {strength.description}
                </p>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/core-strengths" className="btn-primary">
              Explore Our Capabilities
            </Link>
          </div>
        </div>
      </section>

      {/* Project Scale */}
      <section className="section-padding" style={{ background: 'var(--bg-secondary)' }}>
        <div className="container">
          <div className="text-center">
            <h2 className="hero-medium mb-6">Healthcare Infrastructure at Scale</h2>
            <p className="body-large mb-12" style={{ color: 'var(--text-secondary)', maxWidth: '900px', margin: '0 auto' }}>
              From Primary Health Centres to PG Institutes of up to 3600 beds. Temporary facilities up to 2500 beds. Complete compliance with national and international standards.
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
              <div>
                <div className="hero-large mb-2">3600+</div>
                <p className="body-large" style={{ color: 'var(--text-secondary)' }}>Bed Capacity</p>
              </div>
              <div>
                <div className="hero-large mb-2">All Levels</div>
                <p className="body-large" style={{ color: 'var(--text-secondary)' }}>Primary to Tertiary Care</p>
              </div>
              <div>
                <div className="hero-large mb-2">Pan India</div>
                <p className="body-large" style={{ color: 'var(--text-secondary)' }}>Project Execution</p>
              </div>
            </div>

            <Link to="/project-types" className="btn-primary">
              View Project Types
            </Link>
          </div>
        </div>
      </section>

      {/* Major Customers */}
      <section className="section-padding" style={{ background: 'var(--bg-primary)' }}>
        <div className="container">
          <div className="text-center mb-12">
            <h2 className="hero-medium mb-4">
              <span style={{ color: 'var(--brand-teal)' }}>Trusted By</span>
              {' '}
              <span style={{ color: 'var(--brand-orange)' }}>Leading Institutions</span>
            </h2>
            <p className="body-large" style={{ color: 'var(--text-secondary)', maxWidth: '800px', margin: '0 auto' }}>
              Partnering with government bodies, municipal corporations, PSUs, and premier healthcare institutions across India
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {majorCustomersData.map((customer) => (
              <div 
                key={customer.id}
                className="customer-card"
                style={{
                  background: 'var(--bg-secondary)',
                  padding: '24px 20px',
                  borderRadius: '8px',
                  border: '1px solid var(--border-light)',
                  textAlign: 'center',
                  transition: 'all 0.3s ease',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minHeight: '140px'
                }}
              >
                {/* Icon based on type */}
                <div 
                  style={{
                    width: '48px',
                    height: '48px',
                    borderRadius: '50%',
                    background: customer.type === 'Government' ? 'rgba(0, 128, 128, 0.1)' : 
                               customer.type === 'Municipal' ? 'rgba(255, 153, 0, 0.1)' : 
                               customer.type === 'PSU' ? 'rgba(37, 99, 235, 0.1)' : 'rgba(16, 185, 129, 0.1)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginBottom: '12px'
                  }}
                >
                  {customer.type === 'Government' && <Landmark size={24} style={{ color: 'var(--brand-teal)' }} />}
                  {customer.type === 'Municipal' && <Building2 size={24} style={{ color: 'var(--brand-orange)' }} />}
                  {customer.type === 'PSU' && <Building2 size={24} style={{ color: '#2563eb' }} />}
                  {customer.type === 'Private' && <Hospital size={24} style={{ color: '#10b981' }} />}
                </div>
                
                {/* Short Name / Acronym */}
                <h4 
                  className="heading-3 mb-1" 
                  style={{ 
                    color: customer.type === 'Government' ? 'var(--brand-teal)' : 
                           customer.type === 'Municipal' ? 'var(--brand-orange)' : 
                           customer.type === 'PSU' ? '#2563eb' : '#10b981',
                    fontWeight: '600'
                  }}
                >
                  {customer.shortName}
                </h4>
                
                {/* Full Name */}
                <p 
                  className="body-small" 
                  style={{ 
                    color: 'var(--text-secondary)',
                    lineHeight: '1.4',
                    maxWidth: '180px'
                  }}
                >
                  {customer.name}
                </p>
              </div>
            ))}
          </div>

          {/* Customer Type Legend */}
          <div 
            className="flex flex-wrap justify-center gap-6 mt-10"
            style={{ paddingTop: '24px', borderTop: '1px solid var(--border-light)' }}
          >
            <div className="flex items-center gap-2">
              <div style={{ width: '12px', height: '12px', borderRadius: '50%', background: 'var(--brand-teal)' }}></div>
              <span className="body-small" style={{ color: 'var(--text-secondary)' }}>Government</span>
            </div>
            <div className="flex items-center gap-2">
              <div style={{ width: '12px', height: '12px', borderRadius: '50%', background: 'var(--brand-orange)' }}></div>
              <span className="body-small" style={{ color: 'var(--text-secondary)' }}>Municipal Corporation</span>
            </div>
            <div className="flex items-center gap-2">
              <div style={{ width: '12px', height: '12px', borderRadius: '50%', background: '#2563eb' }}></div>
              <span className="body-small" style={{ color: 'var(--text-secondary)' }}>PSU</span>
            </div>
            <div className="flex items-center gap-2">
              <div style={{ width: '12px', height: '12px', borderRadius: '50%', background: '#10b981' }}></div>
              <span className="body-small" style={{ color: 'var(--text-secondary)' }}>Private Hospital</span>
            </div>
          </div>
        </div>
      </section>

      {/* Partner Companies */}
      <section className="section-padding" style={{ background: 'var(--bg-secondary)' }}>
        <div className="container">
          <div className="text-center mb-12">
            <div className="flex items-center justify-center gap-3 mb-4">
              <Handshake size={36} style={{ color: 'var(--brand-orange)' }} />
              <h2 className="hero-medium">
                <span style={{ color: 'var(--brand-orange)' }}>Partner</span>
                {' '}
                <span style={{ color: 'var(--brand-teal)' }}>Companies</span>
              </h2>
            </div>
            <p className="body-large" style={{ color: 'var(--text-secondary)', maxWidth: '800px', margin: '0 auto' }}>
              We are proud to be associated with world-class medical equipment manufacturers and healthcare technology leaders
            </p>
          </div>

          {/* Partner Grid */}
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-7 gap-4">
            {partnerCompaniesData.map((partner) => (
              <div 
                key={partner.id}
                className="partner-card"
                style={{
                  background: 'var(--bg-primary)',
                  padding: '20px 12px',
                  borderRadius: '8px',
                  border: '1px solid var(--border-light)',
                  textAlign: 'center',
                  transition: 'all 0.3s ease',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minHeight: '90px'
                }}
              >
                {/* Standard Icon */}
                <div 
                  style={{
                    width: '36px',
                    height: '36px',
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, var(--brand-teal) 0%, var(--brand-orange) 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginBottom: '10px'
                  }}
                >
                  <Building2 size={18} style={{ color: 'white' }} />
                </div>
                
                {/* Partner Name */}
                <h4 
                  className="body-small" 
                  style={{ 
                    color: 'var(--text-primary)',
                    fontWeight: '600',
                    lineHeight: '1.3',
                    textTransform: 'uppercase',
                    letterSpacing: '0.3px',
                    fontSize: '11px'
                  }}
                >
                  {partner.name}
                </h4>
              </div>
            ))}
          </div>

          {/* Partner Count Summary */}
          <div 
            className="text-center mt-10"
            style={{ paddingTop: '24px', borderTop: '1px solid var(--border-light)' }}
          >
            <p className="body-large" style={{ color: 'var(--text-secondary)' }}>
              <span style={{ color: 'var(--brand-teal)', fontWeight: '600', fontSize: '24px' }}>28+</span>
              <span style={{ marginLeft: '8px' }}>Trusted Technology Partners Worldwide</span>
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding cta-section-dark">
        <div className="container text-center">
          <h2 className="hero-medium mb-4 cta-heading-light">
            Ready to Transform Healthcare Infrastructure?
          </h2>
          <p className="body-large mb-8 cta-text-light">
            Let&apos;s discuss your healthcare project requirements and deliver excellence together.
          </p>
          <Link 
            to="/contact" 
            className="btn-primary btn-light-on-dark"
          >
            Request Project Consultation
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Home;
